import * as React from "react";
import { connect } from "react-redux";

import * as S from "@styled";
import * as _ from "lodash";

import { loader } from "@monaco-editor/react";
import { languageProvider } from "../../shared/monaco/lang/languageProvider";

import { IDisposable } from "monaco-editor";
import { ICompletionTypes } from "../../shared/monaco/model/model";

import { closeProcessVariablesEditorModal, updateProcessVariables } from "../actions/ProcessListActions";

import { DispatchType } from "../../shared/utilities";
import { State } from "../model/State";
import { ProcessWithStatus } from "../model/ProcessWithStatus";
import { addNewEnvironmentVariable, updateProcessVariableValue as updateProcessVariableValueDraft } from "../actions/ProcessVariablesEditorActions";
import CustomModal from "../../shared/components/CustomModal";
import { SchemaType } from "../../environmentVariables/model/VariableSchema";
import { ProcessVariableEdit } from "../model/ProcessVariableEdit";
import { ProcessVariableValueEditor } from "processEditor/components/ProcessVariablesPanel";
import { GetProcessVariableErrorId, ProcessVarValidationErrorType } from "../../processEditor/actions/ValidateProcessVariables";
import { ProcessVariableItemContainer } from "../../processEditor/components/ProcessVariablesPanel";

const ProcessVariablesEditorModal: React.FC<ProcessVariablesEditorProps> = (props) => {
    const mountedRef = React.useRef(false);
    const disposableLanguageProviderRef = React.useRef<IDisposable | null>(null);

    const registerLanguageProvider = async () => {
        const initializedMonaco = await loader.init();

        const completionTypes: ICompletionTypes = {
            environmentVariables: props.environmentVariables,
            processReferences: [],
            returnVariables: [],
            triggers: [],
            variables: [],
            snippets: [],
            processVariables: [],
            apiSpecPaths: [],
        }

        disposableLanguageProviderRef.current = languageProvider.registerLanguageProvider(initializedMonaco, completionTypes);
    };

    const disposeLanguageProvider = () => {
        disposableLanguageProviderRef.current?.dispose();
        disposableLanguageProviderRef.current = null;
    };

    React.useEffect(() => {
        if (!mountedRef.current) {
            registerLanguageProvider();
        }

        mountedRef.current = true;

        return () => disposeLanguageProvider();
    }, []);

    React.useEffect(() => {
        if (mountedRef.current && disposableLanguageProviderRef.current) {
            disposeLanguageProvider();
            registerLanguageProvider();
        }
    }, [props.environmentVariables]);

    const anyErrors = props.processVariables.some(processVariable => processVariable.validationErrors.length > 0);

    return <CustomModal
        label="processVariablesEditorModal"
        onClose={props.onCancel}
        shouldCloseOnOverlayClick={true}
        show={true}
        size={"smaller"}
        modalTitle={<h2>Edit Process Variables</h2>}
    >
        <S.ParameterFieldset style={{ width: '100%' }}>
            {props.processVariables.map(processVariable => {
                const valueErrors = getErrorsOfType(processVariable, ProcessVarValidationErrorType.Value);
                const referenceErrors = getErrorsOfType(processVariable, ProcessVarValidationErrorType.Reference);

                return <ProcessVariableItemContainer key={processVariable.name}>
                    <ProcessVariableValueEditor
                        label={processVariable.name}
                        processVariable={processVariable}
                        valueErrors={valueErrors}
                        referenceError={referenceErrors.length ? referenceErrors[0] : undefined}
                        updateVariableValue={props.onUpdateProcessVariableValueDraft}
                        onAddEnvironmentVariable={(props.onAddEnvironmentVariable)}
                        forbiddenEnvVarNames={props.environmentVariables}
                        disableEdit={false}
                        onlyEditValue={true}
                        showModeSelector={true}
                    />
                </ProcessVariableItemContainer>
            })}
        </S.ParameterFieldset>
        <S.ButtonGroup gap={35} $justifyContentEnd={true}>
            <S.Button variant={S.ButtonType.bordered} onClick={props.onCancel}>Cancel</S.Button>
            <S.Button disabled={anyErrors || !props.isDirty} onClick={props.onSave}>Save</S.Button>
        </S.ButtonGroup>
    </CustomModal>;
}

export const getErrorsOfType = (processVariable: ProcessVariableEdit, errorType: ProcessVarValidationErrorType) => {
    return processVariable.validationErrors.filter(validationError =>
        validationError.elementId === GetProcessVariableErrorId(processVariable, errorType));
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ProcessVariablesEditorModalState {
}

interface ProcessVariablesEditorModalOwnProps {
    process: ProcessWithStatus | undefined;
}

interface ProcessVariablesEditorModalStateProps {
    processVariables: ProcessVariableEdit[];
    environmentVariables: string[];
    isDirty: boolean;
}

interface ProcessVariablesEditorModalDispatchProps {
    onUpdateProcessVariableValueDraft: (id: number, newValue: string) => void;
    onSave: () => void;
    onCancel: () => void;
    onAddEnvironmentVariable: (type: SchemaType, name: string, parentName: string | undefined, value: string | boolean | string[] | undefined) => void;
}

type ProcessVariablesEditorProps = ProcessVariablesEditorModalOwnProps & ProcessVariablesEditorModalStateProps & ProcessVariablesEditorModalDispatchProps;

const mapStateToProps = (state: State): ProcessVariablesEditorModalStateProps => {
    return {
        processVariables: state.processVariablesEditorState.processVariables,
        isDirty: state.processVariablesEditorState.isDirty,
        environmentVariables: state.environmentVariables,
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: ProcessVariablesEditorModalOwnProps): ProcessVariablesEditorModalDispatchProps => {
    return {
        onSave: () => dispatch(updateProcessVariables(ownProps.process?.id)),
        onCancel: () => dispatch(closeProcessVariablesEditorModal()),
        onUpdateProcessVariableValueDraft: (id: number, newValue: string) => dispatch(updateProcessVariableValueDraft(id, newValue)),
        onAddEnvironmentVariable: (type: SchemaType, name: string, parentName: string | undefined, value: string | boolean | string[] | undefined) =>
            dispatch(addNewEnvironmentVariable(type, name, parentName, value)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProcessVariablesEditorModal);
